import React, { forwardRef, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import RetailerContext from '../../context';

import { 
  Box, 
  Button, 
  Dialog, 
  DialogContent, 
  IconButton, 
  Slide, 
  Stack,
  Typography 
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

// Import the training program images
import foodserviceImage from '../../shared/img/popups/shutterstock_1169755372-3000x2000.jpg';
import badgesImage from '../../shared/img/popups/CCBTP_Badges.png';
import foodserviceLogo from '../../shared/img/popups/food-service-training-logo.png';
import retailLogo from '../../shared/img/popups/retail-training-logo.png';

const Transition = forwardRef(function Transition(props, ref) {
  return (
    <Slide
      direction="up"
      ref={ref}
      timeout={10000}
      unmountOnExit
      {...props}
    />
  );
});

const DialogTraining = ({ 
  programData = {
    type: 'foodservice',
    title: 'FOODSERVICE CERTIFIED CANADIAN BEEF TRAINING PROGRAM',
    description: 'The CCBTP Foodservice is designed for front-of-house and back-of-house foodservice professionals and students in culinary-focused educational programs.',
    image: foodserviceImage,
    website: 'fs.ccbtp.ca',
    websiteUrl: 'https://fs.ccbtp.ca'
  }
}) => {
  const { t } = useTranslation();
  const { retailer } = useContext(RetailerContext);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
    // Save to localStorage that user closed the dialog
    localStorage.setItem('trainingDialogClosed', 'true');
  };

  // Function to get the appropriate training logo based on program type
  const getTrainingLogo = () => {
    return programData.type === 'foodservice' ? foodserviceLogo : retailLogo;
  };

  // Auto-open dialog if retailer.show_training is true and user hasn't closed it before
  useEffect(() => {
    const hasUserClosedDialog = localStorage.getItem('trainingDialogClosed') === 'true';
    
    if (retailer?.show_training && !hasUserClosedDialog) {
      setOpen(true);
    }
  }, [retailer?.show_training]);

  // Don't render anything if retailer.show_training is false
  if (!retailer?.show_training) {
    return null;
  }

  return (
    <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth={'xs'}
        fullWidth={true}
        sx={{
          overflow: 'visible',
          borderRadius: '1rem',
        }}
      >
        <DialogContent
          sx={{
            position: 'relative',
            padding: 0,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          }}
        >
          <Stack
            spacing={2}
            direction={'row'}
            sx={{
              backgroundColor: '#000',
              color: 'white',
              px: '16px'
            }}
          >
            <Box
              sx={{
                width: '30rem',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: '-30px',
                }}
              >
                <img
                  src={getTrainingLogo()}
                  alt="Training Program Logo"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    maxWidth: '100%'
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                px: 1,
                py: 3,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontStyle: 'italic',
                  fontSize: {
                    xs: '0.75rem',
                    md: '1rem',
                    // lg: '1.25rem',
                  }
                }}
              >
                {programData.description}
              </Typography>
            </Box>

            <Box>
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Stack>

          <Box>
            <img
              src={programData.image}
              alt={programData.title}
              style={{
                width: '100%',
                height: '400px',
                objectFit: 'cover',
                display: 'block',
                '@media (max-width: 960px)': {
                  height: '300px'
                }
              }}
            />
          </Box>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="flex-start"
              spacing={1}
              sx={{
                pl: 2,
                py: 2,
              }}
            >
              <Typography>
                {t('Learn more about Canadian beef in a retail setting, with the opportunity to become a Certified Canadian Beef Professional.')}
              </Typography>
              
              <Button
                variant="contained"
                href={programData.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: '0',
                  backgroundColor: '#C31E39',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'lowercase',
                  flexGrow: 0,
                  boxShadow: '0 4px 12px rgba(195, 30, 57, 0.3)',
                  '&:hover': {
                    backgroundColor: '#A01830',
                    boxShadow: '0 6px 20px rgba(195, 30, 57, 0.4)',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                {programData.website}
              </Button>
            </Stack>

            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img 
                src={badgesImage} 
                alt="CCBTP Certification Badges"
                style={{ width: '100%', maxWidth: '100%', height: 'auto' }}
              />
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
  );
};

DialogTraining.propTypes = {
  programData: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    website: PropTypes.string.isRequired,
    websiteUrl: PropTypes.string.isRequired
  })
};

export default DialogTraining;
